/**
 * PunchListEntry — view/comment-only punch list for the client (Construction Gates Phase D
 * trimmings). Project-scoped (not milestone-linked): entry.punchItems is attached by
 * fetchProjectPunchItems (api/_shareDocs.js) whenever a `punch_list`-type entry exists on the
 * journey. Owners never see or control punch status internals (no history, no attribution) —
 * just item / area / status / a thumbnail — and never control punch state; they can only leave a
 * comment (the generic 'comment' response kind).
 *
 * data: { intro?: markdown }
 */

import { useState } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, PrimaryButton, DoneChip } from '../journeyUI.jsx'

const STATUS_COLORS = { open: '#b3402f', in_progress: '#c79a3a', ready_for_review: '#1a6fa8', verified: '#1D9E75', closed: '#7a9aaa' }
const STATUS_TEXT = { open: 'Open', in_progress: 'In Progress', ready_for_review: 'Ready for Review', verified: 'Verified', closed: 'Closed' }

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || '#7a9aaa'
  return (
    <span style={{
      fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, padding: '2px 9px', borderRadius: 20,
      border: '0.5px solid ' + color + '55', color, whiteSpace: 'nowrap',
    }}>{STATUS_TEXT[status] || status}</span>
  )
}

function PunchRow({ item }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
      {item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '0.5px solid rgba(7,39,45,0.12)' }} />
      ) : (
        <div style={{ width: 56, height: 56, borderRadius: 6, flexShrink: 0, background: 'rgba(7,39,45,0.05)' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: '#07272D' }}>
            {item.locationTag || 'Punch item'}
          </span>
          <StatusPill status={item.status} />
        </div>
        {item.note && <div style={{ ...J.body, fontSize: 13, marginTop: 3 }}>{item.note}</div>}
      </div>
    </div>
  )
}

export default function PunchListEntry({ entry, response, onRespond }) {
  const items = Array.isArray(entry.punchItems) ? entry.punchItems : []
  const [text, setText] = useState('')
  const commented = response && response.kind === 'comment'

  function submit() {
    const t = text.trim()
    if (!t) return
    onRespond('comment', { text: t })
    setText('')
  }

  return (
    <div>
      <div style={J.eyebrow}>Punch List</div>
      <h2 style={J.title}>{entry.title || 'Punch List'}</h2>
      <hr style={J.rule} />
      {entry.data?.intro && <div style={J.body}><Markdown text={entry.data.intro} /></div>}
      {items.length === 0 ? (
        <div style={{ ...J.mono, marginTop: entry.data?.intro ? 16 : 0 }}>Nothing recorded yet.</div>
      ) : (
        <div style={{ marginTop: entry.data?.intro ? 20 : 4 }}>
          {items.map(it => <PunchRow key={it.id} item={it} />)}
        </div>
      )}
      <div style={{ marginTop: 28 }}>
        {commented && (
          <div style={{ marginBottom: 12 }}>
            <DoneChip>Comment sent</DoneChip>
            {response.payload?.text && <div style={{ ...J.body, fontSize: 13, marginTop: 8, fontStyle: 'italic' }}>&ldquo;{response.payload.text}&rdquo;</div>}
          </div>
        )}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Leave a note or question about the punch list…"
          rows={3}
          style={{
            width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 14, lineHeight: 1.6,
            color: '#07272D', background: 'rgba(255,255,255,0.55)', border: '0.5px solid rgba(7,39,45,0.2)',
            borderRadius: 8, padding: '10px 12px', resize: 'vertical', boxSizing: 'border-box', outline: 'none',
          }}
        />
        <div style={{ marginTop: 10 }}>
          <PrimaryButton onClick={submit} disabled={!text.trim()}>Send comment</PrimaryButton>
        </div>
      </div>
    </div>
  )
}
